import React, { useState } from 'react';
import { Icon, SelectMenu, Typography } from '@scale/scaleui';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import {
  ChatPipelineShortCircuitDestination,
  ChatPipelineShortCircuitOverrideType,
  ChatPipelineShortCircuitOverride,
} from '@scale/shared-types';
import { ChatPipelineShortCircuitOverrideTypeSchemaMap } from '@scale/shared/pipelinev3/genai';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface ShortCircuitOverridesBuilderProps {
  name: string;
  existingValue?: Array<ChatPipelineShortCircuitOverride>;
  updateProperty: (arg: Array<ChatPipelineShortCircuitOverride>) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

interface ShortCircuitOverrideOption {
  id: ChatPipelineShortCircuitOverrideType;
  name: string;
}

const shortCircuitOverrideOptions: Array<ShortCircuitOverrideOption> = [
  {
    id: ChatPipelineShortCircuitOverrideType.SendToAttempt,
    name: 'Send to Attempt for a Different Worker',
  },
  {
    id: ChatPipelineShortCircuitOverrideType.SendToReview,
    name: 'Send to Review',
  },
];

const ShortCircuitOverridesBuilder: React.FC<ShortCircuitOverridesBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const shortCircuitOverrides = existingValue;
  const [selectedType, setSelectedType] = useState<ChatPipelineShortCircuitOverrideType>(
    ChatPipelineShortCircuitOverrideType.SendToAttempt,
  );

  const addShortCircuitOverride = (type: ChatPipelineShortCircuitOverrideType) => {
    const newShortCircuitOverrides = [...shortCircuitOverrides];
    newShortCircuitOverrides.push({
      type,
      numSBQsToActivate: 0,
      ...(type === ChatPipelineShortCircuitOverrideType.SendToAttempt && {
        clearResponse: false,
      }),
      ...(type === ChatPipelineShortCircuitOverrideType.SendToReview && {
        destination: ChatPipelineShortCircuitDestination.L11,
      }),
    } as any);
    updateProperty(newShortCircuitOverrides);
  };

  const getShortCircuitOverrideTag = (shortCircuitOverride: ChatPipelineShortCircuitOverride) => {
    if (shortCircuitOverride.type === ChatPipelineShortCircuitOverrideType.SendToReview) {
      return `send to ${shortCircuitOverride.destination}`;
    } else {
      return shortCircuitOverride.clearResponse ? 'wipe and send to attempt' : 'send to attempt';
    }
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-lg">{name}</div>}
      <div className="prose text-sm italic mb-4">
        Short circuit overrides apply when a certain number of SBQs are reached. They will have
        priority over routing overrides. Multiple short circuit overrides can be configured
        together. For example, one short circuit override could activate on the 2nd SBQ while the
        second activates on the 4th SBQ. Applying the first override does not reset the SBQ count
        for the second override, meaning a task with a total of 4 SBQs would end up activating both
        overrides.
      </div>
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={shortCircuitOverrides}
        setElements={updateProperty}
        getKey={shortCircuitOverride => JSON.stringify(shortCircuitOverride)}
        getTitle={shortCircuitOverride => shortCircuitOverride.type}
        getSchema={shortCircuitOverride =>
          ChatPipelineShortCircuitOverrideTypeSchemaMap[shortCircuitOverride.type]
        }
        getTags={shortCircuitOverride => [getShortCircuitOverrideTag(shortCircuitOverride)]}
        fullWidth
        delimiter={<Typography.Subtitle4>OR</Typography.Subtitle4>}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={shortCircuitOverrideOptions}
          selectedOptionId={selectedType}
          setSelected={value => {
            setSelectedType(value as ChatPipelineShortCircuitOverrideType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addShortCircuitOverride(selectedType)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default ShortCircuitOverridesBuilder;
